import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import {
  AcceptInviteResponse,
  AuthTokenResponse,
  LoginResponse,
  PermissionsResponse,
  RegistrationResponse,
  SetCompanyResponse,
  TokenResponse,
  acceptInviteLink,
  getPermissions,
  getUserWithCompany,
  inviteUser,
  login,
  logoutRequest,
  setCompany,
  signUpOwner,
  signUpUser,
  verifyTwoFactorLogin,
} from '@/api';

import { keyConst, twoFactorConst } from '@/constants';

import {
  CompanyBasicInfo,
  GetPermissionsForm,
  ITwoFactorLoginForm,
  InviteUserForm,
  SetCompanyRequest,
  SignInForm,
  SignUpForm,
  UserBasicInfo,
  UserWithCompanyResponse,
} from '@/interface';

import { clearAuthenticatedSession, persistAuthenticatedSession } from '@/utils/authSession';
import { scheduleTokenRefresh, stopTokenRefresh } from '@/utils/tokenRefresh';

// Import login API

import { clearSentryUserContext, setSentryUserContext } from '@/sentry';

type PermissionLevel = 0 | 1;

interface Permissions {
  add_user?: PermissionLevel;
  register_company?: PermissionLevel;
  create_role?: PermissionLevel;

  [key: string]: PermissionLevel | undefined; // allows dynamic keys
}

interface AuthErrorPayload {
  message: string;
  [key: string]: unknown;
}

const toAuthErrorPayload = (error: unknown, fallbackMessage: string): AuthErrorPayload => {
  const data = axios.isAxiosError(error) ? (error.response?.data as Partial<AuthErrorPayload> | undefined) : undefined;
  return { ...data, message: data?.message || fallbackMessage };
};

interface AuthState {
  userId: string | null;
  companyId: string | null;
  userToken: string | null;
  userInfoFetched: boolean;
  loading: boolean;
  error: AuthErrorPayload | null;
  isUserAuthenticated: boolean;
  user: UserBasicInfo | null;
  company: CompanyBasicInfo | null;
  companies: CompanyBasicInfo[];
  success: boolean;
  inviteStatus: string | null;
  inviteEmail: string | null;
  inviteCompany: string | null;
  inviteProjectId: string | null;
  permission: Permissions;
  projectPermissions?: Permissions;
  companyPermissions?: Permissions;
  userPermissions?: Permissions;
  sidebarPermission?: Permissions;
  hasProjectPermissions: boolean;
}
interface DecodedToken {
  userId: string;
  companyId?: string;
  projectId?: string;
  email?: string;
  username?: string;
  userType?: string;
  role?: string;
}

const storedToken = localStorage.getItem(keyConst.userToken);
const getStoredTokenPayload = (): DecodedToken | null => {
  if (!storedToken) return null;

  try {
    return jwtDecode<DecodedToken>(storedToken);
  } catch {
    return null;
  }
};
const storedTokenPayload = getStoredTokenPayload();

const initialState: AuthState = {
  userId: storedTokenPayload?.userId ?? null,
  companyId: storedTokenPayload?.companyId ?? null,
  userToken: storedToken,
  loading: false,
  userInfoFetched: false,
  error: null,
  user: null,
  company: null,
  success: false,
  inviteStatus: null,
  inviteEmail: '',
  inviteCompany: '',
  inviteProjectId: null,
  permission: {},
  companies: [],
  isUserAuthenticated: Boolean(storedTokenPayload),
  hasProjectPermissions: false,
};

const hasAccessToken = (response: LoginResponse | RegistrationResponse | AuthTokenResponse): response is AuthTokenResponse =>
  'access_token' in response;

const isTwoFactorRequired = (response: LoginResponse): response is Extract<LoginResponse, { requiresTwoFactor: true }> => {
  const value = response as Record<string, unknown>;
  return value[twoFactorConst.requiresTwoFactor] === true;
};

const isTwoFactorSetupRequired = (
  response: LoginResponse | RegistrationResponse,
): response is Extract<LoginResponse | RegistrationResponse, { requiresTwoFactorSetup: true }> => {
  const value = response as Record<string, unknown>;
  return value[twoFactorConst.requiresTwoFactorSetup] === true;
};

export const signUpOwnerRequest = createAsyncThunk<
  RegistrationResponse,
  { data: SignUpForm; navigate: (path: string, options?: { state?: unknown }) => void },
  { rejectValue: AuthErrorPayload }
>('auth/ownerSignUp', async ({ data, navigate }, { rejectWithValue }) => {
  try {
    const response = await signUpOwner(data);
    if (isTwoFactorSetupRequired(response.data)) {
      navigate(twoFactorConst.setupPath, {
        state: {
          setupToken: response.data.setupToken,
          qrCodeDataUrl: response.data.qrCodeDataUrl,
          message: response.data.message,
        },
      });
    } else {
      navigate('/');
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(toAuthErrorPayload(error, 'Signup failed'));
  }
});
export const signUpUserRequest = createAsyncThunk<
  RegistrationResponse,
  { data: SignUpForm; navigate: (path: string, options?: { state?: unknown }) => void },
  { rejectValue: AuthErrorPayload }
>('auth/userSignUp', async ({ data, navigate }, { rejectWithValue }) => {
  try {
    const response = await signUpUser(data);
    if (isTwoFactorSetupRequired(response.data)) {
      navigate(twoFactorConst.setupPath, {
        state: {
          setupToken: response.data.setupToken,
          qrCodeDataUrl: response.data.qrCodeDataUrl,
          message: response.data.message,
        },
      });
    } else {
      navigate('/');
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(toAuthErrorPayload(error, 'Signup failed'));
  }
});

export const getUserWithCompanyRequest = createAsyncThunk<
  UserWithCompanyResponse | null,
  void,
  { rejectValue: AuthErrorPayload }
>('user/getUserWithCompany', async (_, { getState, rejectWithValue }) => {
  const state = getState() as { auth: AuthState };

  if (state.auth.userInfoFetched) {
    return null;
  }
  try {
    const response = await getUserWithCompany();
    return response.data;
  } catch (error) {
    return rejectWithValue(toAuthErrorPayload(error, 'Failed to fetch user with company'));
  }
});
export const signInRequest = createAsyncThunk<
  LoginResponse,
  { data: SignInForm; navigate: (path: string, options?: { state?: unknown }) => void },
  { rejectValue: AuthErrorPayload }
>('auth/ownerSignIn', async ({ data, navigate }, { rejectWithValue }) => {
  try {
    const response = await login(data);
    if (isTwoFactorRequired(response.data)) {
      navigate(twoFactorConst.verifyPath, {
        state: {
          temporaryToken: response.data.temporaryToken,
          message: response.data.message,
        },
      });
    } else if (isTwoFactorSetupRequired(response.data)) {
      navigate(twoFactorConst.setupPath, {
        state: {
          setupToken: response.data.setupToken,
          qrCodeDataUrl: response.data.qrCodeDataUrl,
          message: response.data.message,
        },
      });
    } else {
      navigate('/');
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(toAuthErrorPayload(error, 'Signin failed'));
  }
});

export const verifyTwoFactorLoginRequest = createAsyncThunk<
  TokenResponse,
  ITwoFactorLoginForm & { navigate: (path: string) => void },
  { rejectValue: AuthErrorPayload }
>('auth/verifyTwoFactorLogin', async ({ temporaryToken, code, navigate }, { rejectWithValue }) => {
  try {
    const response = await verifyTwoFactorLogin({ temporaryToken, code });
    navigate('/');
    return response.data;
  } catch (error) {
    return rejectWithValue(toAuthErrorPayload(error, 'Invalid authenticator code'));
  }
});

// getPermissiona
export const getPermissionsRequest = createAsyncThunk<
  PermissionsResponse,
  { data: GetPermissionsForm },
  { rejectValue: AuthErrorPayload }
>('auth/getPermissionsRequest', async ({ data }, { rejectWithValue }) => {
  try {
    const response = await getPermissions(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(toAuthErrorPayload(error, 'Signin failed'));
  }
});
//invite user

export const inviteUserRequest = createAsyncThunk<unknown, { data: InviteUserForm }, { rejectValue: AuthErrorPayload }>(
  'auth/inviteUser',
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await inviteUser(data);

      return response.data;
    } catch (error) {
      return rejectWithValue(toAuthErrorPayload(error, 'Signin failed'));
    }
  },
);
//accept invite
export const verifyInviteLink = createAsyncThunk<AcceptInviteResponse, string, { rejectValue: AuthErrorPayload }>(
  'auth/invite',
  async (token, { rejectWithValue }) => {
    try {
      const res = await acceptInviteLink(token);
      return res.data;
    } catch (err) {
      return rejectWithValue(toAuthErrorPayload(err, 'Invalid or expired invite link'));
    }
  },
);
export const setCompanyRequest = createAsyncThunk<
  SetCompanyResponse,
  SetCompanyRequest & { navigate: (path: string) => void },
  { rejectValue: AuthErrorPayload }
>('auth/setCompany', async ({ companyId, navigate }, { rejectWithValue }) => {
  try {
    const response = await setCompany({ companyId });
    navigate('/');
    return response.data;
  } catch (error) {
    return rejectWithValue(toAuthErrorPayload(error, 'Failed to set company'));
  }
});
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      const accessToken = localStorage.getItem(keyConst.userToken);
      state.userId = null;
      state.userToken = null;
      state.companyId = null;
      state.isUserAuthenticated = false;
      state.inviteProjectId = null;
      state.inviteStatus = null;
      state.inviteEmail = null;
      state.inviteCompany = null;
      clearSentryUserContext();
      clearAuthenticatedSession();
      void logoutRequest(accessToken).finally(() => {
        window.location.href = '/';
      });
    },
    applyAuthenticatedSession: (state, action: PayloadAction<{ token: string; rememberMe?: boolean }>) => {
      const { token, rememberMe } = action.payload;
      persistAuthenticatedSession(token, rememberMe);
      state.userToken = token;

      try {
        const decoded = jwtDecode<DecodedToken>(token);
        state.userId = decoded.userId;
        state.companyId = decoded.companyId ?? null;
        state.isUserAuthenticated = true;
        setSentryUserContext({
          id: decoded.userId,
          email: decoded.email,
          username: decoded.username,
          companyId: decoded.companyId,
          role: decoded.role || decoded.userType,
        });
      } catch {
        state.error = { message: 'Invalid token payload' };
      }
    },
    setAuthState: (state, action: PayloadAction<{ userId: string; companyId?: string | null; userToken?: string | null }>) => {
      state.userId = action.payload.userId;
      state.companyId = action.payload.companyId ?? null;
      if (action.payload.userToken !== undefined) {
        state.userToken = action.payload.userToken;
        if (action.payload.userToken) {
          scheduleTokenRefresh(action.payload.userToken);
        } else {
          stopTokenRefresh();
        }
      }
      state.isUserAuthenticated = true;
      setSentryUserContext({
        id: action.payload.userId,
        companyId: action.payload.companyId ?? null,
      });
    },
    applyRefreshedToken: (state, action: PayloadAction<string>) => {
      const token = action.payload;
      persistAuthenticatedSession(token);
      state.userToken = token;
      try {
        const decoded = jwtDecode<DecodedToken>(token);
        state.userId = decoded.userId;
        state.companyId = decoded.companyId ?? null;
        state.isUserAuthenticated = true;
      } catch {
        state.error = { message: 'Invalid refreshed token' };
      }
    },
    resetError: (state) => {
      state.error = null;
    },
    resetInviteSuccesss: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle Sign Up
      .addCase(signUpOwnerRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUpOwnerRequest.fulfilled, (state, action) => {
        state.loading = false;
        if (isTwoFactorSetupRequired(action.payload)) return;

        const { UserInfo, access_token } = action.payload;
        state.userToken = access_token;
        persistAuthenticatedSession(access_token, Boolean(action.payload.rememberMe));
        state.userId = UserInfo.userId;
        state.companyId = UserInfo.companyId ?? null;
        state.permission = Object.fromEntries(
          Object.entries(UserInfo.permission ?? {}).map(([key, value]) => [key, value as PermissionLevel]),
        );
        state.isUserAuthenticated = true;
        setSentryUserContext({
          id: UserInfo.userId,
          email: UserInfo.email,
          username: UserInfo.username,
          companyId: UserInfo.companyId,
          role: UserInfo.role || UserInfo.userType,
        });
      })
      .addCase(signUpOwnerRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      // Handle Sign In
      .addCase(signInRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInRequest.fulfilled, (state, action) => {
        state.loading = false;
        const rememberMe = Boolean(action.meta.arg.data.rememberMe);

        if (!hasAccessToken(action.payload)) {
          localStorage.setItem(keyConst.rememberMe, String(rememberMe));
          return;
        }

        const token = action.payload.access_token;
        state.userToken = token;
        persistAuthenticatedSession(token, rememberMe);

        const decoded = jwtDecode<DecodedToken>(token);

        if (decoded) {
          state.userId = decoded.userId;
          state.companyId = decoded.companyId ?? null;
          state.isUserAuthenticated = true;
          setSentryUserContext({
            id: decoded.userId,
            email: decoded.email,
            username: decoded.username,
            companyId: decoded.companyId,
            role: decoded.role || decoded.userType,
          });
        } else {
          state.error = { message: 'Invalid token payload' };
        }
      })
      .addCase(signInRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(verifyTwoFactorLoginRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyTwoFactorLoginRequest.fulfilled, (state, action) => {
        state.loading = false;

        const token = action.payload.access_token;
        state.userToken = token;
        persistAuthenticatedSession(token, Boolean(action.payload.rememberMe));

        const decoded = jwtDecode<DecodedToken>(token);
        state.userId = decoded.userId;
        state.companyId = decoded.companyId ?? null;
        state.isUserAuthenticated = true;
        setSentryUserContext({
          id: decoded.userId,
          email: decoded.email,
          username: decoded.username,
          companyId: decoded.companyId,
          role: decoded.role || decoded.userType,
        });
      })
      .addCase(verifyTwoFactorLoginRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      //invite user handle
      .addCase(inviteUserRequest.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(inviteUserRequest.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(inviteUserRequest.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload ?? null;
      })
      // handle accept invite
      .addCase(verifyInviteLink.fulfilled, (state, action) => {
        state.inviteStatus = action.payload.status;
        state.inviteEmail = action.payload.email;
        state.inviteCompany = action.payload.companyName ?? null;
        state.inviteProjectId = action.payload.projectId ?? null;
      })
      .addCase(verifyInviteLink.rejected, (state, action) => {
        state.error = action.payload ?? null;
      })
      // register user
      .addCase(signUpUserRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUpUserRequest.fulfilled, (state, action) => {
        state.loading = false;
        if (isTwoFactorSetupRequired(action.payload)) return;

        const { UserInfo, access_token } = action.payload;
        state.userToken = access_token;
        persistAuthenticatedSession(access_token, Boolean(action.payload.rememberMe));
        state.userId = UserInfo.userId;
        state.companyId = UserInfo.companyId ?? null;
        state.permission = Object.fromEntries(
          Object.entries(UserInfo.permission ?? {}).map(([key, value]) => [key, value as PermissionLevel]),
        );
        state.isUserAuthenticated = true;
        setSentryUserContext({
          id: UserInfo.userId,
          email: UserInfo.email,
          username: UserInfo.username,
          companyId: UserInfo.companyId,
          role: UserInfo.role || UserInfo.userType,
        });
      })

      .addCase(signUpUserRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(getPermissionsRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPermissionsRequest.fulfilled, (state, action) => {
        state.loading = false;

        const projectPermissions = action.payload.projectPermissions || {};
        const companyPermissions = action.payload.companyPermissions || {};
        const userPermissions = action.payload.userPermissions || {};
        const requestedProjectId = action.meta.arg?.data?.projectId;

        if (requestedProjectId) {
          state.projectPermissions = projectPermissions;
          state.hasProjectPermissions = Object.keys(projectPermissions).length > 0;
        }
        const hasCompanyPermissions = Object.keys(companyPermissions).length > 0;
        const hasUserPermissions = Object.keys(userPermissions).length > 0;

        if (hasCompanyPermissions || hasUserPermissions) {
          state.companyPermissions = companyPermissions;
          state.userPermissions = userPermissions;

          const effectivePermissions = hasCompanyPermissions ? companyPermissions : userPermissions;
          state.permission = Object.fromEntries(
            Object.entries(effectivePermissions).map(([key, value]) => [key, value as PermissionLevel]),
          );
          const combinedSidebarPermissions = { ...companyPermissions, ...userPermissions };
          state.sidebarPermission = Object.fromEntries(
            Object.entries(combinedSidebarPermissions).map(([key, value]) => [key, value as PermissionLevel]),
          );
        }
      })
      .addCase(getPermissionsRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(setCompanyRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setCompanyRequest.fulfilled, (state, action) => {
        state.loading = false;

        const token = action.payload.access_token;

        try {
          const decoded = jwtDecode<DecodedToken>(token);

          state.userToken = token;
          persistAuthenticatedSession(token);

          state.userId = decoded.userId;
          state.companyId = decoded.companyId ?? null;
          const rawCompanyPermissions = action.payload?.userInfo?.companyPermissions ?? {};
          state.permission = Object.fromEntries(
            Object.entries(rawCompanyPermissions).map(([key, value]) => [key, value as PermissionLevel]),
          );
          state.isUserAuthenticated = true;
          setSentryUserContext({
            id: decoded.userId,
            email: decoded.email,
            username: decoded.username,
            companyId: decoded.companyId,
            role: decoded.role || decoded.userType || action.payload?.userInfo?.role,
          });
        } catch (err) {
          console.error('JWT decode failed:', err);
          state.error = { message: 'Invalid token' };
          state.isUserAuthenticated = false;
        }
      })

      .addCase(setCompanyRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(getUserWithCompanyRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserWithCompanyRequest.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload) {
          const { user, company, companies } = action.payload;

          state.userId = user?.id || null;
          state.companyId = company?.id || null;
          state.user = user || null;
          state.company = company || null;
          state.companies = companies || [];
          state.isUserAuthenticated = true;
          state.userInfoFetched = true;
          setSentryUserContext({
            id: user?.id,
            email: user?.email,
            username: user?.username,
            companyId: company?.id,
            role: user?.role,
          });
        }
      })
      .addCase(getUserWithCompanyRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      });
  },
});

export const { logout, setAuthState, applyRefreshedToken, applyAuthenticatedSession, resetError, resetInviteSuccesss } =
  authSlice.actions;
